import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import ApEditorMode from '../../../enums/ApEditorMode';
import useAsyncCreateApPlanAction from '../../../hooks/useAsyncCreateApPlanAction';
import useAsyncCreateApTemplateAction from '../../../hooks/useAsyncCreateApTemplateAction';
import { ApActionType } from '../../../sic-lib/enums/ApActionType';
import { RequestStatus } from '../../../sic-lib/enums/RequestStatus';
import { ApEditorActions } from '../../../sic-lib/reducers/actionPlanEditorReducer';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import {
    buildCreateApPlanAction,
    CreateApPlanActionRequest,
} from '../../../sic-lib/types/ApPlanAction';
import {
    buildCreateApTemplateAction,
    CreateApTemplateActionRequest,
} from '../../../sic-lib/types/ApTemplateAction';
import { sanitizeAllowedTags } from '../../../sic-lib/utils/sanitizeHtml';
import { Subtract } from '../../../sic-lib/utils/typeUtils';
import ComposeEmailFields from '../../../types/ComposeEmailFields';
import ComposeEmailRaw from '../../Contact/Communicator/ComposeEmail/ComposeEmailRaw/ComposeEmailRaw';
import ButtonLoading from '../../Shared/ButtonLoading';
import TextFieldFast from '../../Shared/TextFieldFast/TextFieldFast';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        height: '100%',
    },
    headerWrapper: {
        margin: theme.spacing(2, 0, 1),
        [theme.breakpoints.up(0)]: {
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1, 2),
        },
    },
    titleWrapper: {
        [theme.breakpoints.up(0)]: {
            margin: theme.spacing(2, 0),
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            margin: theme.spacing(1, 0),
            padding: theme.spacing(1, 2),
        },
    },
    bodyWrapper: {
        [theme.breakpoints.up(0)]: {
            margin: theme.spacing(2, 0),
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            margin: theme.spacing(1, 0),
            padding: theme.spacing(2),
        },
    },
    buttonWrapper: {
        [theme.breakpoints.up(0)]: {
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1, 2),
        },
    },
}));

interface ComponentState extends ComposeEmailFields {
    body: string;
    title: string;
}

type WithOnSaveHandler = {
    showLoading: boolean;
    onSaveHandler: (title: string, subject: string, body: string) => void;
};

type Props = WithOnSaveHandler & {
    rootId: number;
    actionIndex: number;
    apEditMode: ApEditorMode;
    action: { type: ApActionType };
    apDispatch: React.Dispatch<ApEditorActions>;
};

function ApEditorActionCreate({
    rootId,
    action,
    actionIndex,
    apEditMode,
    apDispatch,
    showLoading,
    onSaveHandler,
}: Props) {
    const { classes } = useStyles();

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            subject: '',
            editor: EditorState.createEmpty(),
            body: '',
            title: '',
        },
    );

    const handleCancel = () => {
        apDispatch({
            type: 'SET_EDITOR_EDIT',
            actionIndex: -1,
            setEdit: false,
        });
    };

    const handleSave = () => {
        let body =
            action.type === ApActionType.email
                ? sanitizeAllowedTags(stateToHTML(compState.editor.getCurrentContent()))
                : compState.body;

        onSaveHandler(compState.title, compState.subject, body);
    };

    return (
        <div className={classes.root}>
            <div className={classes.headerWrapper}>
                <Typography variant="h6" component="h3">
                    {`New ${action.type} Action`}
                </Typography>
            </div>
            <div className={classes.titleWrapper}>
                <TextFieldFast
                    fieldName="title"
                    fieldValue={compState.title}
                    label="Action Title"
                    onChangeHandler={compDispatch}
                />
            </div>
            <div className={classes.bodyWrapper}>
                {action.type === ApActionType.email && (
                    <ComposeEmailRaw
                        subjectValue={compState.subject}
                        editorState={compState.editor}
                        onUpdateHandler={compDispatch}
                        isDisabled={false}
                    />
                )}
                {action.type !== ApActionType.email && (
                    <TextFieldFast
                        fieldName="body"
                        fieldValue={compState.body}
                        label="Message Body"
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={4}
                        onChangeHandler={compDispatch}
                    />
                )}
            </div>
            <div className={classes.buttonWrapper}>
                <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={2}
                >
                    <Grid item xs="auto">
                        <Button onClick={handleCancel}>Cancel</Button>
                    </Grid>
                    <Grid item xs="auto">
                        <ButtonLoading
                            color="secondary"
                            isLoading={showLoading}
                            onClick={handleSave}
                        >
                            Save
                        </ButtonLoading>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
}

/**
 * withSaveTemplateAction
 *
 * @description
 * A Higher-Order-Component which injects the implementation for saving a new action plan template action
 * into it's wrapped component
 */
function withSaveTemplateAction(Component: (props: Props) => React.ReactElement) {
    return function WithSaveTemplateAction(props: Subtract<Props, WithOnSaveHandler>) {
        const { requestStatus, asyncCreateApTemplateAction } = useAsyncCreateApTemplateAction();

        //const [formError, setFormError] = React.useState<Record<string, string> | null>(null);

        const handleSave = async (title: string, subject: string, body: string) => {
            try {
                const action = buildCreateApTemplateAction({
                    ...(props.action as CreateApTemplateActionRequest),
                    ...{ title, subject, body },
                });

                const asyncResult = await asyncCreateApTemplateAction(props.rootId, action);

                if (asyncResult) {
                    props.apDispatch({
                        type: 'MODIFY_EDITOR_ACTION',
                        action: asyncResult,
                        actionIndex: props.actionIndex,
                    });
                }
            } catch (error: any) {
                if (error.isFormError) {
                    // TODO: add form validation to buildCreateApTemplateAction
                }
            }
        };

        return (
            <Component
                {...props}
                showLoading={requestStatus === RequestStatus.active}
                onSaveHandler={handleSave}
            />
        );
    };
}

/**
 * withSavePlanAction
 *
 * @description
 * A Higher-Order-Component which injects the implementation for saving a new action plan action
 * into it's wrapped component
 */
function withSavePlanAction(Component: (props: Props) => React.ReactElement) {
    return function WithSavePlanAction(props: Subtract<Props, WithOnSaveHandler>) {
        const { requestStatus, asyncCreateApPlanAction } = useAsyncCreateApPlanAction();

        //const [formError, setFormError] = React.useState<Record<string, string> | null>(null);

        const handleSave = async (title: string, subject: string, body: string) => {
            try {
                const action = buildCreateApPlanAction({
                    ...(props.action as CreateApPlanActionRequest),
                    ...{ title, subject, body },
                });

                const asyncResult = await asyncCreateApPlanAction(props.rootId, action);

                if (asyncResult) {
                    props.apDispatch({
                        type: 'MODIFY_EDITOR_ACTION',
                        action: asyncResult,
                        actionIndex: props.actionIndex,
                    });
                }
            } catch (error: any) {
                if (error.isFormError) {
                    // TODO: add form validation to buildCreateApPlanAction
                }
            }
        };

        return (
            <Component
                {...props}
                showLoading={requestStatus === RequestStatus.active}
                onSaveHandler={handleSave}
            />
        );
    };
}

/**
 * withEitherApAction
 *
 * @descrption
 * A Heigher-Order-Component which renders either the component injected with Ap Plan action or
 * Ap template logic depending on the apEditMode prop
 */
function withEitherApAction<P extends Subtract<Props, WithOnSaveHandler>>(
    ApTemplateCompoent: (props: P) => React.ReactElement,
    ApPlanComponent: (props: P) => React.ReactElement,
) {
    return function WithEitherApActionProps(props: P) {
        return props.apEditMode === ApEditorMode.template ? (
            <ApTemplateCompoent {...props} />
        ) : (
            <ApPlanComponent {...props} />
        );
    };
}

/**
 * CreateApTemplateAction
 *
 * @description
 * Composed ApEditorCreateAction component containing on-save handler logic for saving a new
 * Ap Template action
 */
const CreateApTemplateAction = withSaveTemplateAction(ApEditorActionCreate);

/**
 * CreateApPlanAction
 *
 * @description
 * Composed ApEditorCreateAction component containing on-save handler logic for saving a new
 * Ap Plan action
 */
const CreateApPlanAction = withSavePlanAction(ApEditorActionCreate);

export default withEitherApAction(CreateApTemplateAction, CreateApPlanAction);
